// ─────────────────────────────────────────────────────────────────────────────
// The date picker: a month grid on a sheet of its own, over whatever is already
// open.
//
// ⚠ IT MOUNTS ITSELF AND IS NOT PART OF THE DECLARATIVE RENDER, deliberately.
// Every form on this app keeps its values in the DOM and the whole tree is
// rebuilt on any state change — so routing the picker through the store would
// rebuild the New TODO sheet underneath it and throw away the half-written todo
// the user opened it from. An overlay that appends itself, owns its own little
// state and hands back one date on Save leaves everything beneath it untouched.
//
// The one thing that follows: it must clean up after itself on every exit path.
// `close()` is the only way out and every path goes through it.
// ─────────────────────────────────────────────────────────────────────────────

import { monthGrid, monthOf, monthTitle, quickDates, shiftMonth, toISO, weekdayLabels } from "home/app/mobile/calendar";
import { dueFacts } from "home/app/mobile/due";
import { t } from "home/app/mobile/i18n";
import { bind, el } from "home/ui/mobile/dom";
import { icon } from "home/ui/mobile/icons";

export interface DatePickerOptions {
    /** The date to open on, `YYYY-MM-DD`, or `""` for none. */
    selected: string;
    /** Injected, like everywhere else in this app — the device's clock, read once by the caller. */
    now: number;
    /** `""` means the date was cleared, which is a real answer and not a cancel. */
    onSave: (iso: string) => void;
}

/**
 * Open the picker inside `host`.
 *
 * Returns a function that closes it, for a caller that needs to tear the screen
 * down for its own reasons.
 */
export function openDatePicker(host: HTMLElement, options: DatePickerOptions): () => void {
    let selected = String(options.selected ?? "");
    // Open on the month of the chosen date, or on this one — never on a month
    // nobody asked about.
    let month = monthOf(selected) || monthOf(toISO(new Date(options.now)));

    const today = toISO(new Date(options.now));

    const grid = el("div", { class: "pqm-cal-grid" });
    const title = el("div", { class: "pqm-cal-title" });
    const summary = el("div", { class: "pqm-cal-summary-text" });

    const scrim = el("div", { class: "pqm-scrim pqm-scrim-date" });

    let closed = false;
    const close = (): void => {
        if (closed) return;
        closed = true;
        document.removeEventListener("keydown", onKey, true);
        scrim.remove();
    };

    /** Escape closes, from anywhere — the sheet under this one has focus of its own. */
    const onKey = (event: KeyboardEvent): void => {
        if (event.key === "Escape") {
            event.stopPropagation();
            close();
        }
    };

    const paint = (): void => {
        title.textContent = monthTitle(month);

        const facts = dueFacts(selected, options.now);
        summary.textContent = selected
            ? t("mobileDateDue", "Due {date}", { date: facts.label })
            : t("mobileDateNoDueDate", "No due date");

        grid.replaceChildren(
            ...monthGrid(month).map((cell) => {
                const classes = ["pqm-cal-day"];
                if (cell.outside) classes.push("pqm-cal-outside");
                if (cell.iso === today) classes.push("pqm-cal-today");
                if (cell.iso === selected) classes.push("pqm-cal-selected");
                // Past days stay selectable — dating something in the past is how a
                // missed deadline gets recorded — but they are dimmed, so the grid
                // says which way time runs.
                if (cell.iso < today) classes.push("pqm-cal-past");

                const button = el("button", {
                    class: classes.join(" "),
                    type: "button",
                    "aria-pressed": cell.iso === selected ? "true" : "false",
                    "aria-label": cell.iso,
                    text: String(cell.day),
                });
                bind(button, "click", () => {
                    selected = cell.iso;
                    // ⚠ Picking a leading or trailing day NAVIGATES to its month, as
                    // the design says. Without this the selection would be invisible
                    // the moment it was made.
                    month = monthOf(cell.iso);
                    paint();
                });

                return button;
            }),
        );
    };

    const step = (delta: number) => () => {
        month = shiftMonth(month, delta);
        paint();
    };

    const prev = el("button", {
        class: "pqm-cal-step",
        type: "button",
        "aria-label": t("mobileDatePreviousMonth", "Previous month"),
        text: "‹",
    });
    const next = el("button", {
        class: "pqm-cal-step",
        type: "button",
        "aria-label": t("mobileDateNextMonth", "Next month"),
        text: "›",
    });
    bind(prev, "click", step(-1));
    bind(next, "click", step(1));

    const quick = el(
        "div",
        { class: "pqm-cal-quick" },
        quickDates(options.now).map((option) => {
            const button = el("button", { class: "pqm-pick pqm-pick-chip", type: "button", text: option.label });
            bind(button, "click", () => {
                selected = option.iso;
                month = monthOf(option.iso);
                paint();
            });

            return button;
        }),
    );

    // ⚠ Clear is not decoration: "a due date can be removed — absence is a
    // legitimate value". It saves an EMPTY date rather than cancelling, which is
    // why it sits beside Save and not beside the close button.
    const clear = el("button", { class: "btn btn-ghost", type: "button", text: t("mobileDateClear", "Clear") });
    bind(clear, "click", () => {
        options.onSave("");
        close();
    });

    const save = el("button", { class: "btn btn-primary", type: "button", text: t("mobileDateSave", "Save due date") });
    bind(save, "click", () => {
        options.onSave(selected);
        close();
    });

    // A cross, like every card's close.
    const cancel = el(
        "button",
        { class: "pqm-appbar-icon pqm-sheet-close", type: "button", "aria-label": t("mobileDateCancel", "Cancel") },
        [icon("x", 18)],
    );
    bind(cancel, "click", close);

    const panel = el(
        "div",
        {
            class: "pqm-sheet pqm-cal-sheet",
            role: "dialog",
            "aria-modal": "true",
            "aria-label": t("mobileDateTitle", "Due date"),
        },
        [
            el("div", { class: "pqm-sheet-head" }, [
                el("div", { class: "pqm-h pqm-sheet-title", text: t("mobileDateTitle", "Due date") }),
                cancel,
            ]),
            quick,
            el("div", { class: "pqm-cal-head" }, [prev, title, next]),
            el(
                "div",
                { class: "pqm-cal-weekdays" },
                weekdayLabels().map((day) => el("div", { class: "pqm-cal-weekday", text: day })),
            ),
            grid,
            el("div", { class: "pqm-cal-summary" }, [summary, clear]),
            save,
        ],
    );

    scrim.appendChild(panel);
    bind(scrim, "click", (event) => {
        if (event.target === scrim) close();
    });
    document.addEventListener("keydown", onKey, true);

    paint();
    host.appendChild(scrim);

    return close;
}
